import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

/**
 * RBAC route guard.
 *
 * - Not logged in               → redirect to "/"
 * - requiredPermission given    → check hasPermission(requiredPermission)
 * - requiredRole given (legacy) → check user.role === requiredRole
 * - otherwise                   → render children
 *
 * Usage:
 *   <ProtectedRoute requiredPermission="manage_settings"><Settings /></ProtectedRoute>
 *   <ProtectedRoute requiredRole="admin"><Settings /></ProtectedRoute>
 */
export default function ProtectedRoute({ children, requiredRole, requiredPermission }) {
  const { user, loading, hasPermission } = useAuth()
  const navigate = useNavigate()

  // Global loading spinner is already rendered by AppRoutes
  if (loading) return null

  if (!user) {
    return <Navigate to="/" replace />
  }

  let denied = false
  if (requiredPermission) {
    denied = !hasPermission(requiredPermission)
  } else if (requiredRole) {
    denied = user.role !== requiredRole
  }

  if (denied) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="text-center animate-fade-up">
          <div className="w-12 h-12 rounded-full bg-[var(--color-brand-50, #FCF5EA)] flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-[var(--color-brand-700, #A85E12)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-base font-semibold text-[var(--color-ink-primary, #141414)]">
            无权限访问
          </h2>
          <p className="text-[13px] text-[var(--color-ink-secondary, #6B655D)] mt-1.5">
            {requiredPermission
              ? '您没有访问此页面的权限，如需权限请联系企业主或管理员'
              : `此页面仅限${requiredRole === 'admin' ? '管理员' : ` ${requiredRole} `}角色访问，如需权限请联系团队管理员`}
          </p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-6 px-5 py-2 rounded-lg bg-brand-700 text-white text-[13px] font-semibold hover:bg-brand-800 transition-colors"
          >
            返回工作台
          </button>
        </div>
      </div>
    )
  }

  return children
}
